import React, { useState, useRef, useEffect } from 'react';

const MediaGallery = ({ prod, mainurl }) => {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(null);
  const mediaRefs = useRef([]);
  const galleryRef = useRef(null);

  useEffect(() => {
    if (isFullScreen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isFullScreen]);

  // Safely parse media data
  const getMediaArray = () => {
    try {
      if (!prod?.media) return [];
      const parsed = JSON.parse(prod.media);
      // Ensure parsed result is an array and filter out invalid entries
      if (Array.isArray(parsed)) {
        return parsed.filter((item) => typeof item === 'string' && item.trim() !== '');
      }
      return [];
    } catch (error) {
      console.error('Error parsing media JSON:', error);
      return [];
    }
  };

  const mediaArray = getMediaArray();

  const openFullScreen = (idx) => {
    if (idx >= 0 && idx < mediaArray.length) {
      setSelectedMediaIndex(idx);
      setIsFullScreen(true);
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  };

  const closeFullScreen = () => {
    setIsFullScreen(false);
    const index = selectedMediaIndex;
    setSelectedMediaIndex(null);

    setTimeout(() => {
      if (mediaRefs.current[index]) {
        mediaRefs.current[index].scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center',
        });
      }
    }, 100);
  };

  const showPreviousMedia = () => {
    setSelectedMediaIndex((prev) =>
      prev === 0 ? mediaArray.length - 1 : prev - 1
    );
  };

  const showNextMedia = () => {
    setSelectedMediaIndex((prev) =>
      prev === mediaArray.length - 1 ? 0 : prev + 1
    );
  };

  console.log('prod:', prod);
  console.log('mediaArray:', mediaArray);
  console.log('Generated URL:', mainurl ? `${mainurl}/${mediaArray[0]}` : 'mainurl is invalid');

  return (
    <>
      <div
        ref={galleryRef}
        className="flex gap-2 mt-2 sm:overflow-auto overflow-x-scroll"
      >
        {mediaArray.length > 0 ? (
          mediaArray.map((media, idx) => (
            <div
              key={`${media}-${idx}`} // Use unique key combining media path and index
              ref={(el) => (mediaRefs.current[idx] = el)}
            >
              {media.includes('.mp4') ? (
                <div className="!w-[100px] !h-[100px] bg-yellow-400 rounded-[4px] overflow-hidden">
                  <video
                    src={`${mainurl}/${media}`}
                    className="w-full h-full object-cover cursor-pointer"
                    onClick={() => openFullScreen(idx)}
                    muted
                    playsInline
                  />
                </div>
              ) : (
                <img
                  src={`${mainurl}/${media}`}
                  alt={`media-${idx}`}
                  className="!w-[100px] !h-[100px]  object-cover rounded cursor-pointer overflow-auto"
                  onClick={() => openFullScreen(idx)}
                  onError={(e) => {
                    console.error(`Failed to load image: ${mainurl}/${media}`);
                    e.target.style.display = 'none'; // Hide broken images
                  }}
                />
              )}
            </div>
          ))
        ) : (
          <></>
        )}
      </div>

      {isFullScreen && selectedMediaIndex !== null && mediaArray[selectedMediaIndex] && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-lg flex items-center justify-center z-50 !h-auto">
          <div className="relative max-w-4xl w-full px-4 z-30">
            {mediaArray[selectedMediaIndex].includes('.mp4') ? (
              <video
                src={`${mainurl}/${mediaArray[selectedMediaIndex]}`}
                className="w-full h-full object-contain"
                controls
                autoPlay
                muted
                playsInline
              />
            ) : (
              <img
                src={`${mainurl}/${mediaArray[selectedMediaIndex]}`}
                alt={`full-screen-${selectedMediaIndex}`}
                className="w-full h-full object-contain"
                onError={(e) => {
                  console.error(`Failed to load full-screen image: ${mainurl}/${mediaArray[selectedMediaIndex]}`);
                  e.target.style.display = 'none';
                }}
              />
            )}

            <button
              onClick={closeFullScreen}
              className="absolute top-0 right-4 text-white font-bold bg-black/50 rounded-full p-1"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-x-icon lucide-x"
              >
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>

            {mediaArray.length > 1 && (
              <>
                <button
                  onClick={showPreviousMedia}
                  className="absolute left-1 top-1/2 transform -translate-y-1/2 text-white bg-black/50 rounded-full p-1"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-chevron-left-icon lucide-chevron-left"
                  >
                    <path d="m15 18-6-6 6-6" />
                  </svg>
                </button>
                <button
                  onClick={showNextMedia}
                  className="absolute right-1 top-1/2 transform -translate-y-1/2 text-white bg-black/50 rounded-full p-1"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-chevron-right-icon lucide-chevron-right"
                  >
                    <path d="m9 18 6-6-6-6" />
                  </svg>
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default MediaGallery;